"use client";

import Link from "@/lib/router/link";
import { usePathname } from "@/lib/router/navigation";
import {
  Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent,
  SidebarGroupLabel, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarRail,
} from "@/components/ui/sidebar";
import { Logo } from "@/components/brand/logo";
import { DataSourceBadge } from "@/components/shell/data-source-badge";
import { ProjectSwitcher } from "./project-switcher";
import { projectNavSections, navItemShown } from "@/config/nav";
import { useTenancy } from "@/lib/tenancy/tenancy-context";
import { useAiTestingShown } from "@/lib/hooks/use-ai-testing-available";
import { useWsBase } from "@/lib/tenancy/use-project-path";
import { useProjectSummary } from "@/lib/hooks/use-summary";
import { cn } from "@/lib/utils";

export function ProjectSidebar() {
  const pathname = usePathname();
  // 2.795: project capabilities are decided on THIS project.
  const { can, projectId } = useTenancy();
  // 2.854: the AI testing entries follow the served menu, fail-open.
  const aiShown = useAiTestingShown();
  const base = useWsBase();

  const isActive = (full: string, exact?: boolean) =>
    exact ? pathname === full : pathname === full || pathname.startsWith(full + "/");

  // 2.991 (vision; the old console's nav badges, fed real data): live counts
  // from the project summary (pre-aggregated, SDD §11.3). Shown only above 0,
  // and each says in words what it counts, because the link opens the whole
  // list: open issues beside Issues, queued or running scans beside Scans.
  const summary = useProjectSummary();
  const liveCount = (href: string): { n: number; words: string; pulse?: boolean } | undefined => {
    const s = summary.data;
    if (!s) return undefined;
    if (href === "/issues" && s.openIssues.total > 0) {
      return { n: s.openIssues.total, words: `${s.openIssues.total} open ${s.openIssues.total === 1 ? "issue" : "issues"}` };
    }
    if (href === "/scans" && s.runsActive > 0) {
      return { n: s.runsActive, words: `${s.runsActive} ${s.runsActive === 1 ? "scan" : "scans"} queued or running`, pulse: true };
    }
    return undefined;
  };

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarHeader className="gap-2.5 px-3 pt-3">
        <Link href="/overview" className="flex h-9 items-center group-data-[collapsible=icon]:justify-center">
          <Logo showName className="group-data-[collapsible=icon]:hidden" />
          <Logo showName={false} className="hidden group-data-[collapsible=icon]:flex" />
        </Link>
        <div className="group-data-[collapsible=icon]:hidden">
          <ProjectSwitcher />
        </div>
      </SidebarHeader>

      <SidebarContent className="px-2.5">
        {projectNavSections.map((section, i) => {
          const items = section.items.filter((it) => navItemShown(it, (p) => can(p, projectId || undefined), aiShown));
          if (items.length === 0) return null;
          return (
            <SidebarGroup key={i} className="py-1">
              {section.label && (
                <SidebarGroupLabel className="px-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/70">{section.label}</SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu className="gap-0.5">
                  {items.map((item) => {
                    const full = `${base}${item.href}`;
                    const active = isActive(full, item.exact);
                    const live = liveCount(item.href);
                    return (
                      <SidebarMenuItem key={item.href}>
                        <SidebarMenuButton asChild isActive={active} tooltip={item.title}
                          className={cn("h-9 gap-2.5 rounded-md text-[13px] font-medium transition-colors",
                            active ? "bg-primary/10 text-primary hover:bg-primary/10 hover:text-primary" : "text-foreground/70 hover:bg-accent hover:text-foreground")}>
                          <Link href={full} aria-current={active ? "page" : undefined}>
                            <item.icon className={cn("size-[18px]", active && "text-primary")} />
                            <span>{item.title}</span>
                            {item.badge && <span className="ml-auto rounded bg-primary/10 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-primary">{item.badge}</span>}
                            {!item.badge && live && (
                              <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-muted px-1.5 py-px text-[11px] font-semibold tabular-nums text-foreground/80" title={live.words}>
                                {live.pulse && <span className="size-1.5 rounded-full bg-sev-low" style={{ animation: "lp-pulse 1.6s ease-in-out infinite" }} aria-hidden="true" />}
                                <span aria-hidden="true">{live.n}</span>
                                <span className="sr-only">{live.words}</span>
                              </span>
                            )}
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}
      </SidebarContent>

      <SidebarFooter className="px-3 pb-3 group-data-[collapsible=icon]:hidden">
        <DataSourceBadge />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
